import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import cx from 'clsx';
import { FormattedMessage } from 'react-intl';
import { Link } from '@repo/poly-hooks/components';
import { IconDownloadOutlined } from '@repo/monochrome-icons';
import { useSegment } from '@repo/poly-hooks/segment';
import { STATUS } from 'receivable-invoices-js/react/constants/invoices';
import { ClientPortalButton } from 'receivable-invoices-js/react/components/shared/responsive-view/client-portal-button';
import styles from './actions.module.css';

interface ReceivableInvoicesActionsProps extends ComponentPropsWithoutRef<'div'> {
  invoiceId?: string;
  invoiceStatus?: string;
  sddEnabled?: boolean;
  isClientPortalEnabled?: boolean;
  clientPortalId?: string;
  showClientPortalCTA?: boolean;
  showPayCTA?: boolean;
  hideDownloadButton?: boolean;
  hasAttachments?: boolean;
  pdfUrl?: string;
  downloadAll?: () => void;
  isImported?: boolean;
}

export function ReceivableInvoicesActions({
  invoiceId,
  invoiceStatus,
  sddEnabled,
  clientPortalId,
  isClientPortalEnabled,
  showClientPortalCTA = true,
  showPayCTA = true,
  hideDownloadButton = false,
  hasAttachments = false,
  pdfUrl,
  downloadAll,
  isImported = false,
  className,
  ...props
}: ReceivableInvoicesActionsProps): ReactNode {
  const segment = useSegment();

  const handlePayClick = (): void => {
    segment.track('public-page-invoice_pay_clicked');
  };

  const handleDownloadClick = (): void => {
    segment.track('public-page-invoice_download_clicked');
  };

  const isDraft = invoiceStatus === STATUS.DRAFT;

  const shouldDisplayPayCTA =
    invoiceStatus !== STATUS.PAID && !isDraft && !sddEnabled && showPayCTA && !isImported;

  return (
    <div className={cx(styles.actions, className)} {...props}>
      {shouldDisplayPayCTA && invoiceId ? (
        <Link
          className={cx(styles.payButton, 'btn btn--primary')}
          data-test-invoice-actions-pay
          onClick={handlePayClick}
          to={`/invoices/${invoiceId}/pay`}
        >
          <FormattedMessage id="invoice.preview.cta.pay" />
        </Link>
      ) : null}

      {!hideDownloadButton && (
        <>
          {hasAttachments ? (
            <button
              className="btn"
              data-test-invoice-actions-download-all
              onClick={downloadAll}
              rel="noopener noreferrer"
              type="button"
            >
              <IconDownloadOutlined aria-hidden="true" className="mr-8" />
              <FormattedMessage id="invoice.preview.cta.download-all" />
            </button>
          ) : (
            pdfUrl && (
              <a
                className="btn"
                data-test-invoice-actions-download
                href={pdfUrl}
                onClick={handleDownloadClick}
                rel="noopener noreferrer"
                target="_blank"
                type="button"
              >
                <IconDownloadOutlined aria-hidden="true" className="mr-8" />
                {isDraft ? (
                  <FormattedMessage id="invoice.preview.draft.cta.download" />
                ) : (
                  <FormattedMessage id="invoice.preview.cta.download" />
                )}
              </a>
            )
          )}
        </>
      )}
      {isClientPortalEnabled && showClientPortalCTA ? (
        <ClientPortalButton clientPortalId={clientPortalId} />
      ) : null}
    </div>
  );
}
